import React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { gql, useQuery } from '@apollo/client';
import DashboardView from '../components/DashboardView';
import DashboardLocationView from '../components/DashboardLocationView';
import {
  DashboardAuditSection,
  Dashboard as DashboardData,
} from '../models/dashboard';

// Weekly dashboard (GRI-957) for the caller's company: /dashboard (all locations) and
// /dashboard/locations/:locationId (one location). The selected week is in ?week=YYYY-MM-DD. The API only returns published JSON
// snapshots of the caller's own company, never live data.

// The fields of a dashboard week, shared with the admin's draft preview (DashboardPreview).
export const DASHBOARD_WEEK_FIELDS = `
  weekStart
  weekEnd
  summary {
    average
    reporting
    total
    top {
      locationId
      name
      score
    }
    lowest {
      locationId
      name
      score
    }
  }
  locations {
    locationId
    name
    offline
    score
    categories {
      name
      weight
      score
      points
    }
    audits {
      auditId
      name
      category
      weight
      threshold
      samples
      passed
      passRate
      pass
      points
      maxPoints
    }
  }
`;

export const DASHBOARD_QUERY = gql`
  query Dashboard($weeks: Int) {
    dashboard(weeks: $weeks) {
      publishedThrough
      weeks {
        ${DASHBOARD_WEEK_FIELDS}
      }
    }
  }
`;

export const SECTIONS_QUERY = gql`
  query DashboardLocationSections(
    $locationId: ID!
    $weekStart: String!
    $draft: Boolean
  ) {
    dashboardLocationSections(
      locationId: $locationId
      weekStart: $weekStart
      draft: $draft
    ) {
      auditId
      name
      sopUrl
      rows {
        passed
        issues
        photoUrl
        date
        hour
        time
      }
    }
  }
`;

// The location report with the audit samples of the selected week. Photo links are signed
// for an hour, so they are fetched again on every visit. `draft` (admins) reads them live.
export const LocationReport = ({
  draft = false,
  ...props
}: Omit<React.ComponentProps<typeof DashboardLocationView>, 'sections'> & {
  draft?: boolean;
}) => {
  const { data, error } = useQuery<{
    dashboardLocationSections: DashboardAuditSection[];
  }>(SECTIONS_QUERY, {
    variables: {
      locationId: props.locationId,
      weekStart: props.weeks[props.selected].weekStart,
      draft,
    },
    fetchPolicy: 'network-only',
  });
  return (
    <DashboardLocationView
      {...props}
      // On an error the report shows without its audit sections.
      sections={data?.dashboardLocationSections ?? (error ? [] : undefined)}
    />
  );
};

const Dashboard = () => {
  const { data, loading, error } = useQuery<{
    dashboard: DashboardData | null;
  }>(DASHBOARD_QUERY, {
    variables: { weeks: 8 },
  });
  const { locationId } = useParams<{ locationId?: string }>();
  const { pathname, search } = useLocation();
  const history = useHistory();

  if (loading) {
    return <Message>Loading your weekly report…</Message>;
  }
  if (error) {
    return <Message>Couldn’t load the weekly report: {error.message}</Message>;
  }
  const weeks = data?.dashboard?.weeks || [];
  if (weeks.length === 0) {
    return (
      <Message>
        Your weekly report will appear here once the first week is published.
      </Message>
    );
  }
  const weekParam = new URLSearchParams(search).get('week');
  const selected = Math.max(
    0,
    weeks.findIndex((w) => w.weekStart === weekParam)
  );
  const onSelect = (index: number) =>
    history.replace(`${pathname}?week=${weeks[index].weekStart}`);
  const week = `?week=${weeks[selected].weekStart}`;

  if (locationId) {
    return (
      <LocationReport
        weeks={weeks}
        selected={selected}
        onSelect={onSelect}
        locationId={locationId}
        onBack={() => history.push(`/dashboard${week}`)}
      />
    );
  }
  return (
    <DashboardView
      weeks={weeks}
      selected={selected}
      onSelect={onSelect}
      reportLink={(id) =>
        `/dashboard/locations/${encodeURIComponent(id)}${week}`
      }
    />
  );
};

const Message = styled.p`
  margin: 44px 52px 0;
  color: #595959;
  font-family: Roboto, Helvetica, Arial, sans-serif;
`;

export default Dashboard;
